import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, BookOpen, Check, Lightbulb, Plus, Star } from 'lucide-react'
import type { Evaluation, Profile, Work } from '../../core/types.ts'
import { deriveCategoryId } from '../../core/categorize.ts'
import { Button } from './ui/button.tsx'
import { Input, Select } from './ui/input.tsx'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip.tsx'
import { cn, plural } from '../lib/utils.ts'

type Props = {
  works: Work[]
  profile: Profile
  evaluation: Evaluation
  selected: Set<string>
  locked: boolean
  onToggle: (workId: string) => void
}

type SortKey = 'work' | 'form' | 'category' | 'selected'
type SortState = { key: SortKey; dir: 'asc' | 'desc' } | null

/** Obálka je dekorace — název i autor stojí v textu vedle, proto alt="" a aria-hidden. */
function Cover({ work }: { work: Work }) {
  const src = work.isbn13 ? `/covers/${work.isbn13}.jpg` : null
  return (
    <span
      aria-hidden
      className="grid h-10 w-7 shrink-0 place-items-center overflow-hidden rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--muted)]"
    >
      {src ? (
        <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <BookOpen size={12} className="text-[var(--muted-foreground)]/60" />
      )}
    </span>
  )
}

export function WorkTable({ works, profile, evaluation, selected, locked, onToggle }: Props) {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('')
  const [form, setForm] = useState('')
  // Jeden přepínač místo dvou: „doporučená učitelem" a „pomůže splnit pravidlo"
  // jsou dvě různé věci a dva switche vedle sebe by je slily dohromady.
  const [view, setView] = useState<'all' | 'recommended' | 'helps'>('all')
  const [sort, setSort] = useState<SortState>(null)

  const categoryOf = useMemo(() => new Map(works.map((w) => [w.id, deriveCategoryId(w, profile)])), [works, profile])
  const labelOf = (id: string | null) => profile.categories.find((c) => c.id === id)?.label ?? 'Nezařazeno'
  const formLabel = (w: Work) => profile.forms.find((f) => f.id === w.formId)?.label ?? w.formId

  /**
   * Doporučené = dílo pomůže splnit některé NESPLNĚNÉ pravidlo. U prázdného
   * seznamu tedy nesvítí nic — pomohlo by cokoli — a přepínač je neutrální.
   */
  const suggested = useMemo(() => {
    const cats = new Set(evaluation.unmetCategoryIds)
    const forms = new Set(evaluation.unmetFormIds)
    if (cats.size === 0 && forms.size === 0) return new Map<string, string>()
    const out = new Map<string, string>()
    for (const w of works) {
      if (selected.has(w.id)) continue
      const cat = categoryOf.get(w.id) ?? null
      if (cat && cats.has(cat)) out.set(w.id, labelOf(cat))
      else if (forms.has(w.formId)) out.set(w.id, `chybí ${profile.forms.find((f) => f.id === w.formId)?.label.toLowerCase() ?? w.formId}`)
    }
    return out
  }, [works, evaluation, selected, categoryOf, profile])

  const recommendedCount = useMemo(() => works.filter((w) => w.recommended).length, [works])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return works.filter((w) => {
      if (q && !`${w.author ?? ''} ${w.title}`.toLowerCase().includes(q)) return false
      if (category && categoryOf.get(w.id) !== category) return false
      if (form && w.formId !== form) return false
      if (view === 'recommended' && !w.recommended) return false
      if (view === 'helps' && !suggested.has(w.id)) return false
      return true
    })
  }, [works, query, category, form, view, categoryOf, suggested])

  /**
   * Řazení podle typu dat ve sloupci, ne podle textu: forma a kategorie mají
   * v profilu vlastní pořadí (`order`), takže „seřadit" respektuje totéž
   * pořadí jako filtr výše, ne abecedu. Výběr řadí nevybrané/vybrané.
   */
  const comparators = useMemo<Record<SortKey, (a: Work, b: Work) => number>>(() => {
    const formOrder = (w: Work) => profile.forms.find((f) => f.id === w.formId)?.order ?? Infinity
    const categoryOrder = (w: Work) => profile.categories.find((c) => c.id === categoryOf.get(w.id))?.order ?? Infinity
    return {
      work: (a, b) =>
        (a.author ?? '').localeCompare(b.author ?? '', 'cs', { sensitivity: 'base' }) ||
        a.title.localeCompare(b.title, 'cs', { sensitivity: 'base' }),
      form: (a, b) => formOrder(a) - formOrder(b) || formLabel(a).localeCompare(formLabel(b), 'cs', { sensitivity: 'base' }),
      category: (a, b) =>
        categoryOrder(a) - categoryOrder(b) ||
        labelOf(categoryOf.get(a.id) ?? null).localeCompare(labelOf(categoryOf.get(b.id) ?? null), 'cs', { sensitivity: 'base' }),
      selected: (a, b) => Number(selected.has(a.id)) - Number(selected.has(b.id)),
    }
  }, [profile, categoryOf, selected])

  const sorted = useMemo(() => {
    if (!sort) return visible
    const cmp = comparators[sort.key]
    const dirMul = sort.dir === 'asc' ? 1 : -1
    return [...visible].sort((a, b) => dirMul * cmp(a, b))
  }, [visible, sort, comparators])

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' }
      if (prev.dir === 'asc') return { key, dir: 'desc' }
      return null
    })
  }

  const ariaSortOf = (key: SortKey): 'none' | 'ascending' | 'descending' => {
    if (sort?.key !== key) return 'none'
    return sort.dir === 'asc' ? 'ascending' : 'descending'
  }

  const clearFilters = () => {
    setQuery('')
    setCategory('')
    setForm('')
    setView('all')
  }

  return (
    <div className="min-w-0 flex-1">
      {/* Grid, ne flex-wrap: ovládání se láme tam, kde chceme my, a „Zobrazit"
          nikdy neskončí samo na druhém řádku. Hledání si bere zbylé místo. */}
      <div className="mb-3 grid grid-cols-1 items-end gap-2 no-print sm:grid-cols-2 editor:flex editor:items-end">
        <label className="flex flex-col gap-1 editor:min-w-[15.625rem] editor:flex-1">
          <span className="text-[length:var(--text-xs)] text-[var(--muted-foreground)]">Hledat</span>
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Hledat autora nebo dílo…"
            aria-label="Hledat v kánonu"
          />
        </label>

        <label className="flex flex-col gap-1 editor:w-52">
          <span className="text-[length:var(--text-xs)] text-[var(--muted-foreground)]">Kategorie</span>
          <Select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Filtr kategorie">
            <option value="">Všechny kategorie</option>
            {[...profile.categories].sort((a, b) => a.order - b.order).map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1 editor:w-40">
          <span className="text-[length:var(--text-xs)] text-[var(--muted-foreground)]">Forma</span>
          <Select value={form} onChange={(e) => setForm(e.target.value)} aria-label="Filtr formy">
            <option value="">Všechny formy</option>
            {profile.forms.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1 editor:w-52">
          <span className="text-[length:var(--text-xs)] text-[var(--muted-foreground)]">Zobrazit</span>
          <Select value={view} onChange={(e) => setView(e.target.value as typeof view)} aria-label="Co zobrazit">
            {/* nikdy netvrdit počet, který v tabulce není */}
            <option value="all">Všechna díla ({works.length})</option>
            <option value="recommended" disabled={recommendedCount === 0}>
              Doporučená učitelem ({recommendedCount})
            </option>
            <option value="helps" disabled={suggested.size === 0}>
              Pomůže splnit, co chybí ({suggested.size})
            </option>
          </Select>
        </label>
      </div>

      <div className="overflow-hidden rounded-[var(--radius-xl)] border border-[var(--border)] bg-[var(--card)]">
        {/* Vnitřní rolování až od `sm`: na mobilu má rolovat stránka, ne krabice
            v krabici. Výška se odvozuje od okna, aby se na 16:9 vešla i hlavička.
            scrollbar-gutter: stable vyhradí pruh pro scrollbar napevno, ať
            nepřekrývá tlačítko „Přidat" v posledním sloupci. */}
        <div className="sm:max-h-[min(41.25rem,calc(100dvh-16rem))] sm:overflow-y-auto sm:[scrollbar-gutter:stable]">
          <table className="hidden w-full table-fixed border-collapse text-left sm:table">
            <caption className="sr-only">Kánon literárních děl — {visible.length} zobrazeno z {works.length}</caption>
            <thead className="sticky top-0 z-10 bg-[var(--card)]">
              <tr className="border-b border-[var(--border)] text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-label)] text-[var(--muted-foreground)]">
                {/* Šířky napevno: bez nich si „Autor · Dílo“ vezme skoro všechno
                    a název kategorie se láme do pěti řádků. */}
                <th scope="col" className="px-4 py-2.5" aria-sort={ariaSortOf('work')}>
                  <SortHeader label="Autor · Dílo" sortKey="work" sort={sort} onSort={toggleSort} />
                </th>
                <th scope="col" className="w-24 px-3 py-2.5" aria-sort={ariaSortOf('form')}>
                  <SortHeader label="Forma" sortKey="form" sort={sort} onSort={toggleSort} />
                </th>
                <th scope="col" className="hidden w-56 px-3 py-2.5 md:table-cell" aria-sort={ariaSortOf('category')}>
                  <SortHeader label="Kategorie" sortKey="category" sort={sort} onSort={toggleSort} />
                </th>
                <th scope="col" className="w-32 px-4 py-2.5 text-right" aria-sort={ariaSortOf('selected')}>
                  <SortHeader label="Výběr" sortKey="selected" align="right" sort={sort} onSort={toggleSort} />
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((w) => {
                const isSelected = selected.has(w.id)
                const hint = suggested.get(w.id)
                return (
                  <tr
                    key={w.id}
                    data-selected={isSelected || undefined}
                    data-suggested={hint ? true : undefined}
                    className={cn(
                      'border-b border-[var(--border)] last:border-0 transition-colors',
                      isSelected
                        ? 'bg-[var(--work-selected-surface)] shadow-[inset_3px_0_0_var(--work-selected)]'
                        : 'hover:bg-[var(--muted)]',
                    )}
                  >
                    <td className="px-4 py-[var(--space-3)]">
                      <WorkIdentity work={w} />
                    </td>
                    <td className="px-3 py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--muted-foreground)]">
                      {formLabel(w)}
                    </td>
                    <td className="hidden px-3 py-[var(--space-3)] md:table-cell">
                      {/* Zkracování na jeden řádek uřezávalo dlouhé názvy kategorií
                          uprostřed slova. Napevno tři řádky drží výšku řádku
                          konstantní a text zůstane čitelný celý, nebo aspoň smysluplně.
                          Skutečná kategorie se zobrazuje vždy — algoritmický tip
                          („pomůže splnit, co chybí") ji dřív přepisoval a dílo pak
                          vypadalo, jako by kategorii nemělo. Tip teď nese tlačítko
                          Přidat (viz SelectButton), aby vycentrovaný text kategorie
                          neposouvala ikonka jen na jedné straně. */}
                      <div className="flex h-[2.8125rem] items-center justify-center text-center">
                        <span
                          title={labelOf(categoryOf.get(w.id) ?? null)}
                          className="line-clamp-3 text-[length:var(--text-xs)] leading-[var(--leading-tight)] text-[var(--muted-foreground)]"
                        >
                          {labelOf(categoryOf.get(w.id) ?? null)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-[var(--space-3)] text-right">
                      <SelectButton work={w} isSelected={isSelected} hint={hint} locked={locked} onToggle={onToggle} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* Mobil: tabulka o čtyřech sloupcích se na 390px nevejde a skrývat
              sloupce by znamenalo informaci ztratit. Karta ji udrží celou. */}
          <ul className="sm:hidden">
            {sorted.map((w) => {
              const isSelected = selected.has(w.id)
              const hint = suggested.get(w.id)
              return (
                <li
                  key={w.id}
                  data-selected={isSelected || undefined}
                  data-suggested={hint ? true : undefined}
                  className={cn(
                    'border-b border-[var(--border)] px-4 py-[var(--space-4)] last:border-0',
                    isSelected && 'bg-[var(--work-selected-surface)] shadow-[inset_3px_0_0_var(--work-selected)]',
                  )}
                >
                  <WorkIdentity work={w} />
                  <div className="mt-2.5 flex items-end justify-between gap-3">
                    <div className="min-w-0 flex-1 text-[length:var(--text-xs)] text-[var(--muted-foreground)]">
                      <span>{formLabel(w)} · {labelOf(categoryOf.get(w.id) ?? null)}</span>
                    </div>
                    <div className="shrink-0">
                      <SelectButton work={w} isSelected={isSelected} hint={hint} locked={locked} onToggle={onToggle} />
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>

          {visible.length === 0 && (
            <div className="px-4 py-10 text-center text-[length:var(--text-sm)] text-[var(--muted-foreground)]">
              Hledání nic nenašlo.{' '}
              <button type="button" onClick={clearFilters} className="font-medium text-[var(--primary)] underline">
                Zrušit filtry
              </button>
            </div>
          )}
        </div>
      </div>

      <p className="mt-2 text-[length:var(--text-xs)] text-[var(--muted-foreground)]">
        Zobrazeno {plural(visible.length, 'dílo', 'díla', 'děl')} ze {works.length}.
      </p>
    </div>
  )
}

/**
 * Klikací záhlaví sloupce: šipka nahoru/dolů u aktivního řazení, jinak
 * ztlumená obousměrná šipka jako náznak, že sloupec jde řadit vůbec.
 * Tři stavy (asc → desc → žádné), aby šlo řazení i zrušit.
 */
function SortHeader({
  label, sortKey, sort, onSort, align = 'left',
}: { label: string; sortKey: SortKey; sort: SortState; onSort: (key: SortKey) => void; align?: 'left' | 'right' }) {
  const active = sort?.key === sortKey
  const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={cn(
        'inline-flex items-center gap-1 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-label)] text-[var(--muted-foreground)] hover:text-[var(--foreground)]',
        align === 'right' && 'flex-row-reverse',
      )}
    >
      {label}
      <Icon size={12} aria-hidden className={cn('shrink-0', active ? 'text-[var(--foreground)]' : 'opacity-40')} />
    </button>
  )
}

/** Autor, název a hvězdička. Kreslí to tabulka i mobilní karta, ať se neliší. */
function WorkIdentity({ work }: { work: Work }) {
  return (
    <div className="flex items-center gap-3">
      <Cover work={work} />
      <div className="min-w-0">
        <div className="truncate text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-label)] text-[var(--muted-foreground)]">
          {work.author ?? 'bez autora'}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="truncate font-serif text-[length:var(--text-lg)] font-medium tracking-[var(--tracking-title)]">
            {work.title}
          </span>
          {work.recommended && <RecommendedMark work={work} />}
        </div>
      </div>
    </div>
  )
}

/**
 * Kurátorské doporučení. Hvězdička je vedle názvu, ne ve sloupci Kategorie —
 * ten patří algoritmickému „pomůže splnit, co chybí" a tyhle dvě věci se nesmí
 * slít. Poznámka učitele je v tooltipu i v textu pro odečítač.
 */
function RecommendedMark({ work }: { work: Work }) {
  const label = work.recommendedNote
    ? `Doporučeno: ${work.recommendedNote}`
    : 'Doporučeno'
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="shrink-0 rounded-[var(--radius-sm)] leading-none">
          <Star size={13} aria-hidden className="fill-[var(--work-suggested)] text-[var(--work-suggested)]" />
          <span className="sr-only">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

/**
 * Zamčený stav (components.md §4): tlačítko drží vlastní barvu výběru, jen
 * zesvětlenou. Šedá by říkala „nefunkční“, tohle má říkat „tohle nesmíš ty“.
 * Zůstává fokusovatelné (aria-disabled bez disabled), aby byl tooltip
 * dosažitelný z klávesnice.
 *
 * „Pomůže splnit, co chybí“ (algoritmický tip, ne kurátorské doporučení) bydlí
 * tady, ne ve sloupci Kategorie: tlačítko Přidat je přesně to, co tip vede
 * uživatele udělat, a ikonka mimo vycentrovaný text kategorii neposouvá.
 */
function SelectButton({
  work, isSelected, hint, locked, onToggle,
}: { work: Work; isSelected: boolean; hint?: string; locked: boolean; onToggle: (id: string) => void }) {
  const label = isSelected ? `Odebrat ze seznamu: ${work.title}` : `Přidat do seznamu: ${work.title}`
  const suggested = !!hint && !isSelected

  const button = (
    <Button
      size="sm"
      variant={isSelected ? 'selected' : 'outline'}
      aria-pressed={isSelected}
      aria-label={label}
      aria-disabled={locked || undefined}
      className={cn(
        'min-w-[6.5rem]',
        locked && 'pointer-events-none opacity-45',
        suggested && 'border-[var(--work-suggested)] bg-[var(--work-suggested-surface)] text-[var(--work-suggested)]',
      )}
      onClick={() => !locked && onToggle(work.id)}
    >
      {isSelected ? <><Check size={14} aria-hidden /> Vybráno</> : suggested ? <><Lightbulb size={14} aria-hidden /> Přidat</> : <><Plus size={14} aria-hidden /> Přidat</>}
    </Button>
  )

  if (locked) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-block rounded-[var(--radius-md)]">{button}</span>
        </TooltipTrigger>
        <TooltipContent>Pro úpravy je potřeba heslo</TooltipContent>
      </Tooltip>
    )
  }

  if (suggested) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-block rounded-[var(--radius-md)]">{button}</span>
        </TooltipTrigger>
        <TooltipContent>{hint}</TooltipContent>
      </Tooltip>
    )
  }

  return button
}
